<template>
  <b-list-group>
    <b-form-file
      :accept="acceptedInputTypes.toLocaleString()"
      :placeholder="uuid ? placeholder : $t('fileUpload.saveFirst')"
      :disabled="!uuid || locked || disabled"
      browse-text="Upload"
      multiple
      :class="{ 'no-border': allFiles.length }"
      @input="handleInput"
    />
    <div v-if="!preview">
      <b-list-group-item
        v-for="(file, index) in allFiles"
        :key="index"
        class="d-flex justify-content-between align-items-center px-2 py-1 border-top-0"
      >
        <span><i class="far fa-file mr-2" /> {{ file?.name }}</span>

        <div>
          <b-button
            v-if="!locked && !disabled"
            v-b-tooltip.hover.right
            :title="$t('fileUpload.delete')"
            variant="link"
            size="sm"
            class="float-right"
            @click="removeFile(file.id)"
          >
            <i class="fa-fw fal fa-trash-can text-danger" />
          </b-button>
          <b-button
            v-b-tooltip.hover.right
            title="Download"
            variant="link"
            size="sm"
            class="float-right"
            @click="handleDownload({ file })"
          >
            <i class="icon fa-fw fal fa-download" />
          </b-button>
        </div>
      </b-list-group-item>
    </div>

    <div
      v-if="preview && loaded && allFiles.length"
      class="d-flex flex-wrap"
    >
      <div
        v-for="(file, index) in allFiles"
        :key="index"
        class="flex-grow-1 mb-3"
      >
        <span class="d-flex flex-column bg-light border justify-content-center">
          <b-overlay
            :show="showLoader === file.id"
            variant="white"
            class="d-flex justify-content-center"
          >
            <b-img
              fluid-grow
              :src="imageUrls[file.id]"
              :alt="file.name"
            />
          </b-overlay>
          <b-button
            v-if="!locked && !disabled"
            v-b-tooltip.hover
            :title="$t('fileUpload.delete')"
            variant="link"
            size="sm"
            @click="removeFile(file.id)"
          >
            <i class="fa-fw fal fa-trash-can text-danger" />
          </b-button>
          <b-button
            v-else
            v-b-tooltip.hover
            title="Download"
            variant="link"
            size="sm"
            @click="handleDownload({ file })"
          >
            <i class="icon fa-fw fal fa-download" />
          </b-button>
        </span>
      </div>
    </div>
  </b-list-group>
</template>

<script>
import { getDownloadURL } from '@/services/aws';
import { saveAs } from 'file-saver';
import imageCompression from 'browser-image-compression';
import { mapGetters } from 'vuex'

export default {
  name: 'UploadField',
  props: {
    value: {
      type: [String, Array],
      default: () => []
    },
    uuid: {
      type: String,
      default: ''
    },
    name: {
      type: String,
      default: ''
    },
    locked: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    placeholder: {
      type: String,
      default: '',
    },
    multiple: {
      type: Boolean,
      default: true
    },
    preview: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      acceptedInputTypes: this.preview ? ['image/jpg', 'image/jpeg', 'image/png'] : ['image/jpg', 'image/jpeg', 'image/png', 'application/pdf'],
      downloadableFiles: this.value,
      loaded: false,
      showLoader: -1,
      imageUrls: {},
      compressionOptions: {
        maxSizeMB: 1,
        maxWidthOrHeight: 1920,
        useWebWorker: true,
      },
    }
  },

  computed: {
    ...mapGetters('processes', ['getSelectedProcess']),
    doctype() {
      return this.name.split('.').pop()
    },
    allFiles() {
      return [...this.downloadableFiles]
    }
  },

  watch: {
    value(newValue) {
      this.downloadableFiles = Array.isArray(newValue) ? [...newValue] : []
    },
    downloadableFiles: {
      async handler(files) {
        if (!this.preview) return;
        this.imageUrls = {};
        this.loaded = false
        for (const file of files) {
          this.imageUrls[file.id] = await this.getFileUrl({ id: file.id })
        }

        this.loaded = true
      },
      deep: true
    }
  },

  mounted() {
    this.loaded = false
    this.downloadableFiles = [...this.value]
  },

  methods: {
    handleInput(files) {
      if (!files || !files?.length) return;
      [...files].map(this.addFile);
    },
    async addFile(f) {
      if (this.locked || this.disabled) return;
      const maxUploadSize = 1024 * 1024 * 4.8; // 4.8MB

      const error = this.checkFileType(f);
      if (error) return;

      const compressedFile = /image/.test(f.type) ? await imageCompression(f, this.compressionOptions) : f;

      if (compressedFile.size > maxUploadSize) {
        this.$bvToast.toast(this.$t('fileUpload.fileToBig'), {
          title: this.$t('fileUpload.toBigError'),
          variant: 'danger',
          autoHideDelay: 6000,
          toaster: 'b-toaster-top-right',
          solid: true
        });
        return;
      }

      //this.$store.dispatch('processes/disableSaveRecord', true)

      const formData = new FormData();

      formData.append('type', this.doctype);
      formData.append('file', compressedFile);
      try {
        const api = await fetch(`${process.env.VUE_APP_AWS_API_BASE_URL}/process/realisation/${this.uuid}/files`, {
          method: 'PUT',
          headers: {
            'x-api-key': process.env.VUE_APP_AWS_API_KEY
          },
          body: formData
        });

        if (!api.ok) {
          throw new Error('Upload failed');
        }

        const response = await api.json();

        if (response.data.process) {
          const files = response.data.process.Docs[this.doctype]
          this.$emit('input', files)
          this.downloadableFiles = files;
        }
      } catch (e) {
        this.$bvToast.toast(this.$t('fileUpload.generalError'), {
          title: this.$t('fileUpload.uploadFailed'),
          variant: 'danger',
          autoHideDelay: 10000,
          toaster: 'b-toaster-top-right',
          solid: true
        });
      }
    },

    checkFileType(file) {
      return this.acceptedInputTypes.find(type => type === file.type)
        ? null
        : 'Dit type document kan niet worden geüpload';
    },

    async removeFile(id) {
      if (this.disabled || this.locked) return;
      this.showLoader = id;
      //this.$store.dispatch('processes/disableSaveRecord', true)

      const api = await fetch(`${process.env.VUE_APP_AWS_API_BASE_URL}/process/${this.uuid}/files/${this.doctype}/${id}`, {
        method: 'DELETE',
        headers: {
          'x-api-key': process.env.VUE_APP_AWS_API_KEY
        }
      });

      if (!api.ok) {
        this.showLoader = -1
        return this.$store.dispatch('tenant/setToast', {
          vm: this,
          message: this.$t('fileUpload.generalError'),
          error: true,
          variant: 'danger',
          delay: 100
        });
      }

      const removedFileIndex = this.downloadableFiles.findIndex(file => file.id === id)
      if (removedFileIndex !== -1) {
        this.downloadableFiles.splice(removedFileIndex, 1)
        //this.$emit('input', this.downloadableFiles)
      }

      this.$store.dispatch('tenant/setToast', {
        vm: this,
        message: this.$i18n.t('fileUpload.docRemoved'),
        error: false,
        delay: 100
      });

      this.showLoader = -1
    },

    handleDownload: async function ({ file }) {
      const response = await getDownloadURL({
        uuid: this.uuid,
        index: file.id,
        doctype: this.doctype
      }).catch(e => console.log(e));

      if (!response) { // 404
        return this.$store.dispatch('tenant/setToast', {
          vm: this,
          message: this.$t('fileUpload.fileNotFound', { file: file.name }),
          error: true,
          variant: 'danger',
          delay: 100
        });
      }

      // Chrome, FF, Edge
      if (window.browser && window.browser.downloads) {
        window.browser.downloads.download({
          url: response.url
        });
      } else {
        // Safari, etc.
        const res = await fetch(response.url);
        const blob = await res.blob();
        saveAs(blob, file.name);
      }
    },
    getFileUrl: async function ({ id }) {
      try {
        const response = await getDownloadURL({
          uuid: this.uuid,
          index: id,
          doctype: this.doctype
        });

        return response.url;
      } catch (e) {
        // console.log(e)
      }
    },
  }
}
</script>

<style lang="scss" scoped>
.no-border {
  :deep(.custom-file-label) {
    border-bottom-right-radius: 0;
    border-bottom-left-radius: 0;
  }
}

.custom-file {
  min-height: 41px;

  &:disabled {
    color: black;
    background-color: #e9ecef;

    ~.custom-file-label,
    ~.custom-file-label::after {
      cursor: default;
    }
  }

  .custom-file-label,
  .custom-file-label::after {
    cursor: pointer;
    height: 41px;
    margin: 0;
    display: flex;
    align-items: center;
    padding: 0 0.5rem;
  }
}

:deep(.d-block.form-file-text) {
  display: none !important;
}
</style>
